import { createSignal } from "solid-js";
import { tgHapticImpact, tgHapticNotification } from "./telegram";

const STORAGE_KEY = "alex-tracker:rest-timer";
export const REST_PRESETS = [60, 90, 120, 180] as const;
const DEFAULT_PRESET = 90;

export type RestTimerStatus = "idle" | "running" | "paused" | "finished";

export interface RestTimerState {
  status: RestTimerStatus;
  /** Total duration for this run (seconds) */
  durationSec: number;
  /** Remaining whole seconds (display) */
  remainingSec: number;
  /** Absolute end timestamp (ms) while running */
  endsAt: number | null;
  lastPreset: number;
}

function positiveInt(v: unknown, fallback: number): number {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(1, Math.round(n));
}

function nonNegInt(v: unknown, fallback: number): number {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.round(n));
}

const DEFAULT: RestTimerState = {
  status: "idle",
  durationSec: DEFAULT_PRESET,
  remainingSec: DEFAULT_PRESET,
  endsAt: null,
  lastPreset: DEFAULT_PRESET,
};

function sanitize(raw: Partial<RestTimerState> | null | undefined): RestTimerState {
  const lastPreset = positiveInt(raw?.lastPreset, DEFAULT_PRESET);
  let status: RestTimerStatus = "idle";
  if (
    raw?.status === "running" ||
    raw?.status === "paused" ||
    raw?.status === "finished" ||
    raw?.status === "idle"
  ) {
    status = raw.status;
  }

  let endsAt: number | null = null;
  if (status === "running" && raw?.endsAt != null) {
    const e = Number(raw.endsAt);
    endsAt = Number.isFinite(e) ? e : null;
    if (endsAt == null) status = "idle";
  }

  let remainingSec = nonNegInt(raw?.remainingSec, lastPreset);
  let durationSec = positiveInt(raw?.durationSec, lastPreset);

  if (status === "running" && endsAt != null) {
    remainingSec = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
    if (remainingSec <= 0) {
      status = "finished";
      remainingSec = 0;
      endsAt = null;
    }
  }

  if (status === "idle") {
    remainingSec = lastPreset;
    durationSec = lastPreset;
    endsAt = null;
  }

  if (status === "finished") {
    remainingSec = 0;
    endsAt = null;
  }

  return {
    status,
    durationSec,
    remainingSec,
    endsAt,
    lastPreset,
  };
}

function load(): RestTimerState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT };
    const data = JSON.parse(raw) as Partial<RestTimerState>;
    return sanitize(data);
  } catch {
    return { ...DEFAULT };
  }
}

function persist(s: RestTimerState) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

const [state, setState] = createSignal<RestTimerState>(load());

let tickId: ReturnType<typeof setInterval> | null = null;
let finishedNotified = false;

function apply(next: RestTimerState) {
  const clean = sanitize(next);
  setState(clean);
  persist(clean);
  ensureTick(clean);
}

function ensureTick(s: RestTimerState) {
  if (s.status === "running") {
    if (tickId == null) {
      tickId = setInterval(tick, 200);
    }
  } else if (tickId != null) {
    clearInterval(tickId);
    tickId = null;
  }
}

function tick() {
  const s = state();
  if (s.status !== "running" || s.endsAt == null) return;
  const rem = Math.max(0, Math.ceil((s.endsAt - Date.now()) / 1000));
  if (rem <= 0) {
    if (!finishedNotified) {
      finishedNotified = true;
      notifyFinished();
    }
    apply({
      ...s,
      status: "finished",
      remainingSec: 0,
      endsAt: null,
    });
    return;
  }
  if (rem !== s.remainingSec) {
    apply({ ...s, remainingSec: rem });
  }
}

function notifyFinished() {
  try {
    tgHapticNotification("success");
    tgHapticImpact("heavy");
  } catch {
    /* ignore */
  }
  playBeep();
}

function playBeep() {
  try {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 880;
    gain.gain.value = 0.08;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
    osc.stop(ctx.currentTime + 0.5);
    setTimeout(() => void ctx.close(), 600);
  } catch {
    /* autoplay / unsupported */
  }
}

// Resume ticker if restored as running
ensureTick(state());
if (state().status === "finished") {
  finishedNotified = true;
}

export function restTimerState() {
  return state;
}

export function restTimerStatus() {
  return state().status;
}

export function restRemainingSec() {
  return state().remainingSec;
}

export function startRestTimer(seconds?: number) {
  const base = positiveInt(
    seconds ?? state().lastPreset ?? DEFAULT_PRESET,
    DEFAULT_PRESET,
  );
  finishedNotified = false;
  const endsAt = Date.now() + base * 1000;
  apply({
    status: "running",
    durationSec: base,
    remainingSec: base,
    endsAt,
    lastPreset: base,
  });
  tgHapticImpact("medium");
}

export function pauseRestTimer() {
  const s = state();
  if (s.status !== "running" || s.endsAt == null) return;
  const rem = Math.max(0, Math.ceil((s.endsAt - Date.now()) / 1000));
  apply({
    ...s,
    status: "paused",
    remainingSec: rem,
    endsAt: null,
  });
}

export function resumeRestTimer() {
  const s = state();
  if (s.status !== "paused" || s.remainingSec <= 0) return;
  finishedNotified = false;
  const rem = positiveInt(s.remainingSec, DEFAULT_PRESET);
  apply({
    ...s,
    status: "running",
    remainingSec: rem,
    endsAt: Date.now() + rem * 1000,
  });
}

export function resetRestTimer() {
  const s = state();
  const preset = positiveInt(s.lastPreset, DEFAULT_PRESET);
  apply({
    status: "idle",
    durationSec: preset,
    remainingSec: preset,
    endsAt: null,
    lastPreset: preset,
  });
  finishedNotified = false;
}

export function addRestSeconds(delta: number) {
  const s = state();
  const d = Math.round(delta);
  if (!Number.isFinite(d) || d === 0) return;

  if (s.status === "running" && s.endsAt != null) {
    const endsAt = s.endsAt + d * 1000;
    const rem = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
    apply({
      ...s,
      endsAt,
      remainingSec: rem,
      durationSec: positiveInt(s.durationSec + d, rem || DEFAULT_PRESET),
    });
  } else if (s.status === "paused" || s.status === "idle") {
    const rem = Math.max(1, nonNegInt(s.remainingSec, s.lastPreset) + d);
    apply({
      ...s,
      remainingSec: rem,
      durationSec: Math.max(rem, positiveInt(s.durationSec + d, rem)),
      status: s.status === "idle" ? "idle" : "paused",
    });
  } else if (s.status === "finished") {
    startRestTimer(Math.max(1, d));
  }
}

export function dismissRestFinished() {
  const s = state();
  if (s.status !== "finished") return;
  const preset = positiveInt(s.lastPreset, DEFAULT_PRESET);
  apply({
    status: "idle",
    durationSec: preset,
    remainingSec: preset,
    endsAt: null,
    lastPreset: preset,
  });
  finishedNotified = false;
}

export function formatRestClock(totalSec: number): string {
  const sec = Number.isFinite(totalSec)
    ? Math.max(0, Math.floor(totalSec))
    : 0;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** 0..1 progress remaining (1 = full time left) */
export function restProgress(): number {
  const s = state();
  const dur = s.durationSec;
  if (!Number.isFinite(dur) || dur <= 0) return 0;
  const rem = Number.isFinite(s.remainingSec) ? s.remainingSec : 0;
  return Math.min(1, Math.max(0, rem / dur));
}
